/**
 * WarehousePicker — Searchable warehouse picker with inline "+ Create".
 *
 * Wraps EntityPickerBase + WarehouseQuickCreateDialog so any warehouse FK field
 * can search, pick, and create a tenant-scoped warehouse without leaving the form.
 * Replaces the raw <Select> warehouse combos across inventory/procurement/sales.
 *
 * See docs/design-system/ui-detail-and-form-pages.md § FK Picker with Inline Create.
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Warehouse as WarehouseIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase } from '../common';
import { useWarehouses } from '../../inventory/hooks/useWarehouses';
import { WarehouseQuickCreateDialog } from './WarehouseQuickCreateDialog';
import type { Warehouse } from '../../../modules/inventory/types';

interface WarehousePickerProps {
  /** Currently selected warehouse ID. Empty string = no selection / cleared. */
  value: string;
  /** Callback when a warehouse is selected (empty string when cleared) */
  onChange: (warehouseId: string) => void;
  /** Callback returning the selected warehouse object (null when cleared) */
  onWarehouseSelected?: (warehouse: Warehouse | null) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder text when no warehouse is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** id applied to the trigger button (so an external <Label htmlFor> can target it) */
  triggerId?: string;
  /** Render the trigger with a destructive border (validation error) */
  hasError?: boolean;
  /** Whether to show the clear option */
  allowClear?: boolean;
  /** Custom label for the clear option (e.g. "All warehouses") */
  clearLabel?: string;
  /** Whether the field is required (shows * on label) */
  required?: boolean;
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /** Only show active warehouses (default true) */
  activeOnly?: boolean;
  /** Warehouse IDs to exclude from the list (e.g. the other end of a transfer) */
  excludeIds?: string[];
  /**
   * Restrict the list to warehouses owned by this company. Used by the stock
   * transfer dialog to keep source and destination within one legal entity —
   * cross-company transfers are not supported.
   */
  companyId?: string;
}

const formatWarehouseLabel = (warehouse: Warehouse) =>
  `${warehouse.name} (${warehouse.code})`;

export function WarehousePicker({
  value,
  onChange,
  onWarehouseSelected,
  label,
  placeholder,
  disabled,
  className,
  triggerId,
  hasError,
  allowClear = false,
  clearLabel,
  required = false,
  hideAddNew = false,
  activeOnly = true,
  excludeIds,
  companyId,
}: WarehousePickerProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  // Fetch once (small list) and filter client-side — same query key as other
  // useWarehouses({ is_active }) consumers so React Query dedupes the request.
  const { data: warehousesData } = useWarehouses({
    is_active: activeOnly ? true : undefined,
  });

  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const warehouses = useMemo(() => {
    const visible = (warehousesData ?? []).filter(
      (wh) =>
        !excludeSet.has(wh.id) && (!companyId || wh.company_id === companyId),
    );
    const query = search.trim().toLowerCase();
    if (!query) return visible;
    return visible.filter(
      (wh) =>
        wh.name.toLowerCase().includes(query) ||
        wh.code.toLowerCase().includes(query),
    );
  }, [warehousesData, excludeSet, companyId, search]);

  const handleSelect = useCallback(
    (warehouseId: string) => {
      const warehouse = warehouses.find((wh) => wh.id === warehouseId) ?? null;
      onChange(warehouseId);
      onWarehouseSelected?.(warehouse);
      setOpen(false);
      setSearch('');
    },
    [warehouses, onChange, onWarehouseSelected],
  );

  const handleClear = useCallback(() => {
    onChange('');
    onWarehouseSelected?.(null);
    setOpen(false);
    setSearch('');
  }, [onChange, onWarehouseSelected]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleWarehouseCreated = useCallback(
    (warehouse: Warehouse) => {
      onChange(warehouse.id);
      onWarehouseSelected?.(warehouse);
      setSearch('');
      setShowCreateDialog(false);
    },
    [onChange, onWarehouseSelected],
  );

  return (
    <>
      <EntityPickerBase<Warehouse>
        value={value}
        items={warehouses}
        getItemLabel={formatWarehouseLabel}
        renderItem={(warehouse, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <WarehouseIcon className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{warehouse.name}</span>
            <span className="ms-2 text-xs text-muted-foreground shrink-0">
              {warehouse.code}
              {warehouse.company_name ? ` · ${warehouse.company_name}` : ''}
            </span>
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('warehousePicker.searchOrCreate')}
        searchPlaceholder={t('warehousePicker.searchWarehouses')}
        emptyMessage={t('warehousePicker.noWarehousesFound')}
        disabled={disabled}
        className={className}
        required={required}
        triggerId={triggerId}
        hasError={hasError}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        clearLabel={clearLabel}
        hideAddNew={hideAddNew}
        addNewLabel={
          search.trim()
            ? t('warehousePicker.createWithName', { name: search.trim() })
            : t('warehousePicker.addNewWarehouse')
        }
        onAddNew={handleAddNew}
      />

      <WarehouseQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleWarehouseCreated}
        skipNavigation
        defaultName={createDialogName}
      />
    </>
  );
}
